import queryString from 'query-string';
import React, { useContext } from 'react';

/*
 * Usage:
 * Currently we use a pattern of calling the getParam utility function at the top
 * of each top level js file for grabbing url params and then manually adding them
 * to redux/passing them down as props.
 *
 * This makes that information available to all children components throught the
 * React ContextAPI.
 *
 * Wrap top level parent component with UrlQueryParamContextWrapper
 * eg
 *
 * for a url: https://www.toplevelcomponent.com/?showCom=false
 *
 * <TopLevelComponent /> turns into
 *
 * <UrlQueryParamContextWrapper>
 *  <TopLevelComponent />
 * </UrlQueryParamContextWrapper>
 */

export const UrlQueryParamContext = React.createContext({});

export function UrlQueryParamContextWrapper(props) {
  const urlParams = queryString.parse(location.search, {
    parseBooleans: true,
  });

  return (
    <UrlQueryParamContext.Provider value={urlParams}>
      {props.children}
    </UrlQueryParamContext.Provider>
  );
}

/*
 * Whenever a child of <TopLevelComponent /> needs to access the url param info
 * Just use UrlQueryParamConsumer
 *
 * eg
 * <UrlQueryParamConsumer>
 *  {queryParams => <ChildComponent showCom={queryParams.showCom} />}
 * </UrlQueryParamConsumer>
 */
export const UrlQueryParamConsumer = UrlQueryParamContext.Consumer;

/*
 * React Hook version to consume the context
 * eg.
 *
 * function ChildComponent() {
 *  const showCom = useUrlQueryParam(value => value.showCom);
 * }
 */

export const useUrlQueryParam = (transform = state => state) =>
  transform(useContext(UrlQueryParamContext));
